import React, {ReactNode} from 'react'
import ReactTooltip from 'react-tooltip'
import styled from '@emotion/styled'
import {css} from '@emotion/core'
import {withTheme} from 'emotion-theming'
import {MdInfoOutline as InfoIcon} from 'react-icons/md'

type AppearanceType = 'primary' | 'secondary' | 'dark'

type PropsType = {
  /** Function that will be called after tooltip show, with event that triggered show */
  afterShow?: () => void
  /** The appearance of the Tooltip */
  appearance?: AppearanceType
  /** Tooltip trigger element */
  children: ReactNode
  /** Whether the Tooltip follow the cursor */
  floating?: boolean
  /** The icon shown on the left side of the content */
  icon?: ReactNode
  /** Tooltip unique identification */
  id?: string
  /** Whether the Tooltip trigger is inline or block */
  inline?: boolean
  /** The message to show on Tooltip */
  message?: ReactNode
  /** Tooltip width */
  width?: string
  /** Override Tooltip position */
  overridePosition?: (
    position: {left: number; top: number},
    currentEvent: Event,
    currentTarget: EventTarget,
    // node is the ref argument, and the wrapper
    // is restricted to: div | span
    refNode: null | HTMLDivElement | HTMLSpanElement
  ) => {left: number; top: number}
}

const ARROW_WIDTH = 8

const TooltipElement = styled('div')<{
  width?: string
  appearance?: AppearanceType
}>(
  ({theme, width, appearance}) => css`
    width: ${width};
    display: flex;
    padding: ${theme.layout.spacing.s300}px ${theme.layout.spacing.s500}px
      ${theme.layout.spacing.s300}px ${theme.layout.spacing.s300}px;

    font-size: ${theme.fonts.size.sm};
    line-height: ${theme.fonts.lineHeight.md};
    font-weight: ${theme.fonts.weight.sm};
    font-family: ${theme.fonts.family.default};

    ${
      appearance === 'primary' &&
      `background: ${theme.colors.tooltipPrimaryBg};`
    }

    ${
      appearance === 'secondary' &&
      `background: ${theme.colors.tooltipSecondaryBg};`
    }
    
    ${appearance === 'dark' && `background: ${theme.colors.tooltipDarkBg};`}

    box-shadow: ${theme.shadows.fixed};
    border-radius: ${theme.layout.radius.md}px;

    &::before,
    &::after {
      display: block;
      content: '';
      position: absolute;
      width: ${ARROW_WIDTH}px;
      height: ${ARROW_WIDTH}px;
      top: ${ARROW_WIDTH / 2}px;
      left: calc(50% - ${ARROW_WIDTH / 2}px);
      transform: rotate(45deg);
    }
    &::before {
      box-shadow: ${theme.shadows.fixed};
      z-index: -1;
    }
    &::after {
      ${
        appearance === 'primary' &&
        `background: ${theme.colors.tooltipPrimaryBg};`
      }
  
      ${
        appearance === 'secondary' &&
        `background: ${theme.colors.tooltipSecondaryBg};`
      }
      
      ${appearance === 'dark' && `background: ${theme.colors.tooltipDarkBg};`}

      z-index: 1;
    }
  `
)

const IconWrapper = styled('div')(
  ({theme}) => css`
    margin-right: ${theme.layout.spacing.s300}px;
    vertical-align: middle;

    svg {
      vertical-align: middle;
    }
  `
)

const MessageWrapper = styled('div')<{appearance?: AppearanceType}>(
  ({theme, appearance}) => css`
    margin-top: ${theme.layout.spacing.s100}px;
    ${['secondary', 'dark'].includes(appearance) &&
    `color: ${theme.colors.tooltipSecondaryColor};`}
  `
)

const TooltipTrigger = styled('div')<{inline: boolean}>(
  ({inline}) => css`
    display: ${inline ? 'inline-block' : 'block'};
  `
)

const TooltipIcon = withTheme(({theme}) => (
  <InfoIcon size={24} color={theme.colors.tooltipIcon} />
))

export const Tooltip = ({
  afterShow,
  appearance,
  children,
  id,
  message,
  inline,
  floating,
  icon,
  width,
  overridePosition
}: PropsType) => {
  const effect = floating ? 'float' : 'solid'

  return (
    <>
      <TooltipTrigger inline={inline} data-tip={message} data-for={id}>
        {children}
      </TooltipTrigger>
      {message && (
        <ReactTooltip
          id={id}
          type="light"
          effect={effect}
          place="bottom"
          afterShow={afterShow || null}
          backgroundColor="transparent"
          uuid={id}
          overridePosition={overridePosition}
        >
          <TooltipElement
            appearance={appearance}
            className="tooltip-element"
            width={width}
          >
            {icon && <IconWrapper>{icon}</IconWrapper>}
            <MessageWrapper appearance={appearance}>{message}</MessageWrapper>
          </TooltipElement>
        </ReactTooltip>
      )}
    </>
  )
}

Tooltip.defaultProps = {
  appearance: 'primary',
  floating: false,
  icon: <TooltipIcon />,
  inline: true,
  width: 'auto'
}

export default Tooltip
